import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config/api';
import { chatGameApi } from '../components/ChatGame/chatGameApi';

const MODEL_MAP = {
  'Gemini 3 Flash': 'google/gemini-3-flash-preview',
  'Gemini 2.5 Pro': 'google/gemini-2.5-pro-preview',
  'ChatGPT (GPT-5.1)': 'openai/gpt-5.1-chat',
        'ChatGPT (GPT-5 Mini)': 'openai/gpt-5-mini',
  'Claude Haiku 4.5': 'anthropic/claude-haiku-4.5',
  'Llama 3.3 Nemotron': 'nvidia/llama-3.3-nemotron-super-49b-v1.5',
  'DeepSeek v3.2': 'deepseek/deepseek-v3.2',
  'Qwen3 Max': 'qwen/qwen3-max-thinking',
  'Kimi K2': 'moonshotai/kimi-k2-0905'
};

function AutoPlayPage() {
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    (async () => {
      try {
        setStatus('fetching');
        const response = await fetch(`${API_BASE_URL}/api?getFeaturedCompetition=true`);
        const data = await response.json();
        const result = data.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;
        const template = result?.featured;

        if (!template) {
          setError('No featured competition available right now.');
          setStatus('error');
          return;
        }

        setStatus('creating');

        // Persistent player identifier for leaderboard tracking
        let playerId = localStorage.getItem('competition_player_id');
        if (!playerId) {
          playerId = 'player_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
          localStorage.setItem('competition_player_id', playerId);
        }

        // Build AI player configs from template
        const players = [];
        for (const [modelName, count] of Object.entries(template.ai_models || {})) {
          const modelId = MODEL_MAP[modelName];
          if (!modelId) continue;
          for (let i = 0; i < count; i++) {
            players.push({
              name: count > 1 ? `${modelName} ${i + 1}` : modelName,
              character_description: `An AI competitor in ${template.name}`,
              personality_traits: [],
              objective: template.objective,
              secret_info: '',
              is_human: false,
              model: modelId
            });
          }
        }

        // Add human player — use persistent display name for leaderboard
        const humanName = (() => {
          const stored = localStorage.getItem('competition_player_name');
          if (stored) return stored;
          const adjectives = ['Swift', 'Bold', 'Clever', 'Lucky', 'Witty', 'Sharp', 'Brave', 'Keen', 'Sly', 'Chill'];
          const nouns = ['Fox', 'Hawk', 'Wolf', 'Lynx', 'Raven', 'Tiger', 'Panda', 'Otter', 'Falcon', 'Bear'];
          const name = `${adjectives[Math.floor(Math.random() * adjectives.length)]}${nouns[Math.floor(Math.random() * nouns.length)]}`;
          localStorage.setItem('competition_player_name', name);
          return name;
        })();
        players.push({
          name: humanName,
          character_description: 'A human competitor',
          personality_traits: [],
          objective: template.objective,
          secret_info: '',
          is_human: true,
          model: null
        });

        const isImposter = !!template.imposter_mode;

        const config = {
          name: `${template.name} - Competition`,
          objective: template.objective,
          scene_description: template.scene_description || template.description,
          competitive_mode: true,
          imposter_mode: isImposter,
          ...(isImposter
            ? { imposter_word_rounds: template.imposter_word_rounds || 3 }
            : { total_rounds: template.total_rounds || 3, max_rounds: (template.total_rounds || 3) * 2 }),
          messages_per_round: template.messages_per_round || 4,
          is_shared: true,
          is_featured_competition: true,
          featured_week_key: template.weekKey,
          featured_template_index: template.templateIndex,
          human_player_identifier: playerId,
          interests: [isImposter ? 'imposter' : 'competitive', 'featured'],
          players,
          profileImagesEnabled: true,
          allowAiNameSelection: true,
          ...(template.music_url ? { music_url: template.music_url } : {}),
          ...(template.ai_roast_ammo ? { ai_roast_ammo: template.ai_roast_ammo } : {})
        };

        const gameResult = await chatGameApi.createGameInstant(config);
        const gameId = gameResult.game_id;

        if (gameResult.human_player_id) {
          localStorage.setItem(`humanPlayerId_${gameId}`, gameResult.human_player_id);
        }

        window.location.href = `/game-step?gameId=${encodeURIComponent(gameId)}&loading=true`;
      } catch (err) {
        console.error('[AutoPlayPage] Failed to create game:', err);
        setError(err.message || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    })();
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #0a0a0a 100%)',
      color: '#fff',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      {status === 'error' ? (
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>:(</div>
          <div style={{ fontSize: 18, marginBottom: 24, color: '#ff6b6b' }}>{error}</div>
          <button
            onClick={() => { started.current = false; setStatus('loading'); setError(null); window.location.reload(); }}
            style={{
              padding: '12px 32px',
              fontSize: 16,
              background: 'linear-gradient(135deg, #b8860b, #ffd700)',
              border: 'none',
              borderRadius: 8,
              color: '#1a1a2e',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '3px solid rgba(255,215,0,0.3)',
            borderTopColor: '#ffd700',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 24px'
          }} />
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
            {status === 'fetching' ? 'Loading game...' : 'Creating your game...'}
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>
            You'll be redirected automatically
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </div>
  );
}

export default AutoPlayPage;
